import { useState, useRef, useEffect } from "react";
import {
  Box, Typography, IconButton, Checkbox, Tooltip, Chip,
  FormControlLabel, TextField,
} from "@mui/material";
import {
  Add, Person, Close, AccountTree,
  ZoomIn, ZoomOut, CenterFocusStrong, ChevronRight,
  Edit, Check,
} from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary:   { main: "#1C2B4A" },
    secondary: { main: "#3A7BD5" },
    background:{ default: "#ECEEF3", paper: "#FFFFFF" },
    text:      { primary: "#1C2B4A", secondary: "#6B7A99" },
  },
  typography: { fontFamily: "'DM Sans', sans-serif" },
  shape: { borderRadius: 8 },
});

// ─── Layout constants ─────────────────────────────────────────────────────────
const NODE_W     = 148;   // estimated/min width used for layout spacing
const MAX_NODE_W = 220;   // max card width
const MIN_NODE_W = 100;   // min card width
const NODE_H     = 52;
const ROW_H      = 80;
const H_GAP      = 30;
const ROW_INDENT = 32;
const TOP_PAD    = 40;
const LEFT_PAD   = 140;
const PANEL_W    = 300;
const STEM_OFFSET = 15;

const ROW_LABELS = {
  0: "Category",
  // 1: "Groups / Roles",
  // 2: "Sub-items",
  // 3: "Level 4",
};

const LANE_TINTS = [
  "rgba(255,255,255,0.45)",
  "rgba(255,255,255,0.45)",
  "rgba(255,255,255,0.45)",
  "rgba(255,255,255,0.45)",
  "rgba(255,255,255,0.45)",
];

// ─── Permissions ──────────────────────────────────────────────────────────────
const ALL_PERMISSIONS = {
  App: {
    "Time in Time Out": {},
    Forms: { "Create Forms": {}, "Delete Forms": {} },
    GeoTagging: {},
    "Change Password": {},
    "Device Locking": {},
  },
  Dashboard: { "Activity Menu": {}, Users: {}, "User Log": {} },
};

function flattenPerms(tree, section = null, parent = null, acc = []) {
  for (const [key, children] of Object.entries(tree)) {
    if (section === null) { flattenPerms(children, key, null, acc); }
    else {
      acc.push({ section, key, parent });
      if (Object.keys(children).length) flattenPerms(children, section, key, acc);
    }
  }
  return acc;
}
const FLAT_PERMS = flattenPerms(ALL_PERMISSIONS);

function makePerms(checked = []) {
  const s = {};
  FLAT_PERMS.forEach(p => { s[p.key] = checked.includes(p.key); });
  return s;
}

function uid() { return Math.random().toString(36).slice(2, 9); }

// ─── Initial orgs ─────────────────────────────────────────────────────────────
const makeOrg = (id, label, note = "") => ({
  id, label, note,
  children: [
    {
      id: `${id}-territories`, label: "Territories", type: "category",
      dynamic: true,
      children: [
        {
          id: `${id}-region`, label: "Region", type: "group",
          children: [
            { id: `${id}-batangas`, label: "Batangas",  type: "leaf", children: [] },
            { id: `${id}-ncr`,     label: "NCR",        type: "leaf", children: [] },
            { id: `${id}-northluz`,label: "North Luz",  type: "leaf", children: [] },
          ],
        },
        {
          id: `${id}-region1`, label: "Region 1", type: "group",
          children: [
            { id: `${id}-isabella`, label: "Isabella", type: "leaf", children: [] },
          ],
        },
      ],
    },
    {
      id: `${id}-roles`, label: "Roles", type: "category",
      dynamic: true,
      children: [
        {
          id: `${id}-report-admin`, label: "Report Admin", type: "role",
          permissions: makePerms(["Time in Time Out","Forms","Create Forms","Delete Forms","Activity Menu"]),
          children: [],
        },
        {
          id: `${id}-manager1`, label: "Manager 1", type: "role",
          permissions: makePerms(["Time in Time Out","Forms","Activity Menu"]),
          children: [
            { id: `${id}-fa1`, label: "Foreman A", type: "role", permissions: makePerms(["Time in Time Out"]), children: [] },
            { id: `${id}-fb1`, label: "Foreman B", type: "role", permissions: makePerms(["Time in Time Out"]), children: [] },
          ],
        },
        {
          id: `${id}-manager2`, label: "Manager 2", type: "role",
          permissions: makePerms(["Time in Time Out","Forms","Activity Menu"]),
          children: [
            { id: `${id}-fa2`, label: "Foreman A", type: "role", permissions: makePerms(["Time in Time Out"]), children: [] },
            { id: `${id}-fb2`, label: "Foreman B", type: "role", permissions: makePerms(["Time in Time Out"]), children: [] },
          ],
        },
      ],
    },
    {
      id: `${id}-projects`, label: "Projects", type: "category",
      dynamic: true, children: [],
    },
  ],
});

const initialOrgs = [
  makeOrg("org1", "Organization (Default)", "Main organization"),
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function findNode(tree, id) {
  if (!tree) return null;
  if (tree.id === id) return tree;
  for (const c of (tree.children || [])) { const f = findNode(c, id); if (f) return f; }
  return null;
}
function getAllRoles(node, acc = []) {
  if (node.type === "role") acc.push(node);
  for (const c of (node.children || [])) getAllRoles(c, acc);
  return acc;
}
function getAncestorRoles(tree, targetId, path = []) {
  if (!tree) return null;
  if (tree.id === targetId) return path;
  for (const c of (tree.children || [])) {
    const f = getAncestorRoles(c, targetId, tree.type === "role" ? [...path, tree.id] : path);
    if (f !== null) return f;
  }
  return null;
}

// ─── Layout engine ────────────────────────────────────────────────────────────
// Categories (org's direct children) each get their own sequential row block,
// so Territories rows never mix with Roles rows.
//
// WITHIN each category's subtree, all nodes at the same relative depth share
// the same horizontal row. So Foreman A (under Manager1) and Foreman A (under
// Manager2) appear on the same row — they just advance x left-to-right.
//
// depthBase: the absolute row index assigned to a category. Its children get
// depthBase+1, grandchildren depthBase+2, etc.
//
// X cursor: a global Map<absoluteDepth → nextRawLeft> ensures nodes on the
// same row never overlap, regardless of which parent they belong to.

function computeLayout(orgNode) {
  const positions = new Map();
  const cursor    = new Map(); // absDepth → next raw left-edge

  function getC(d)       { return cursor.get(d) ?? 0; }
  function advC(d, val)  { cursor.set(d, Math.max(getC(d), val)); }

  function estimateNodeW(node) {
    return Math.min(MAX_NODE_W, Math.max(MIN_NODE_W, node.label.length * 9 + 40));
  }

  function subtreeWidth(node) {
    const kids = node.children || [];
    const nw = estimateNodeW(node);
    if (!kids.length) return nw;
    return Math.max(nw,
      kids.reduce((s, c, i) => s + subtreeWidth(c) + (i > 0 ? H_GAP : 0), 0)
    );
  }

  function maxRelDepth(node, d = 0) {
    let m = d;
    for (const c of (node.children || [])) m = Math.max(m, maxRelDepth(c, d + 1));
    return m;
  }

  // absDepth  — which row this node sits on (controls y)
  // relDepth  — depth relative to its category root (controls x indent, starts at 0)
  function place(node, absDepth, relDepth, parentId) {
    const sw      = subtreeWidth(node);
    const rawLeft = getC(absDepth);
    // relDepth 0 = no indent (flush left at LEFT_PAD), 1 = one indent step, etc.
    const x       = LEFT_PAD + rawLeft + relDepth * ROW_INDENT;
    const y       = TOP_PAD  + absDepth * ROW_H;

    positions.set(node.id, {
      x, y,
      cx: x + estimateNodeW(node) / 2,
      cy: y + NODE_H / 2,
      node, parentId,
      rowIndex: absDepth,
      depth: relDepth,
    });

    advC(absDepth, rawLeft + sw + H_GAP);

    const kids = node.children || [];
    if (!kids.length) return;

    const cd = absDepth + 1;
    advC(cd, Math.max(getC(cd), rawLeft));
    for (const child of kids) place(child, cd, relDepth + 1, node.id);
  }

  const cats = orgNode.children || [];
  let depthBase = 1;
  for (const cat of cats) {
    place(cat, depthBase, 0, orgNode.id);   // relDepth starts at 0 for every category
    depthBase += maxRelDepth(cat) + 2;
  }

  return positions;
}


function canvasDimensions(positions) {
  let maxX = 0, maxY = 0;
  positions.forEach(({ x, y }) => {
    maxX = Math.max(maxX, x + MAX_NODE_W + 80);
    maxY = Math.max(maxY, y + NODE_H + 80);
  });
  return { w: Math.max(maxX, 600), h: Math.max(maxY, 400) };
}

// ─── Connectors ───────────────────────────────────────────────────────────────
function ParentConnectors({ parentPos, childPositions }) {
  if (!childPositions.length) return null;
  const stroke = "#94A3B8", sw = 1.5;
  const stemX    = parentPos.x + STEM_OFFSET;
  const stemTopY = parentPos.y + NODE_H;
  const branches = childPositions.map(cp => ({
    branchY: cp.y + NODE_H / 2,
    endX: cp.x,
    isLeaf: cp.node.type === "leaf",
  }));
  const stemBotY = branches[branches.length - 1].branchY;
  return (
    <g>
      <line x1={stemX} y1={stemTopY} x2={stemX} y2={stemBotY} stroke={stroke} strokeWidth={sw}/>
      {branches.map(({ branchY, endX, isLeaf }, i) => (
        <line key={i} x1={stemX} y1={branchY} x2={endX} y2={branchY}
          stroke={stroke}
          strokeWidth={sw}
          strokeDasharray="none"
        />
      ))}
    </g>
  );
}

// ─── Node card style ──────────────────────────────────────────────────────────
function getNodeStyle(node, isSelected) {
  const base = {
    position: "absolute",
    // Content-width: card shrinks/grows with text, capped at MAX_NODE_W
    width: "max-content",
    minWidth: MIN_NODE_W,
    maxWidth: MAX_NODE_W,
    height: NODE_H,
    borderRadius: 10,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "0 14px",
    userSelect: "none",
    transition: "box-shadow .15s, transform .15s",
    boxSizing: "border-box",
    fontFamily: "'DM Sans', sans-serif",
    cursor: "pointer",
  };
  const sel = isSelected;
  const variants = {
    category: { background: sel ? "#EEF6FF" : "#F8FAFC", border: `1.5px solid ${sel ? "#3A7BD5" : "#E2E8F0"}` },
    role:     { background: sel ? "#EEF6FF" : "white",   border: `${sel ? "2px" : "1.5px"} solid ${sel ? "#3A7BD5" : "#E2E8F0"}`, boxShadow: sel ? "0 0 0 3px rgba(58,123,213,0.14)" : "0 1px 4px rgba(28,43,74,0.08)" },
    group:    { background: "white", border: `1.5px solid ${sel ? "#3A7BD5" : "#E2E8F0"}`, boxShadow: "0 1px 3px rgba(28,43,74,0.06)" },
    leaf:     { background: "white", border: `1.5px solid ${sel ? "#3A7BD5" : "#E8ECF2"}`, boxShadow: "0 1px 2px rgba(28,43,74,0.04)" },
  };
  return { ...base, ...(variants[node.type] || variants.leaf) };
}

// ─── NodeCard — hover reveals absolute + buttons (right, bottom, left) ────────
// Buttons are position:absolute, so they NEVER shift layout or siblings.
const BTN_R = 10;

function PlusBtn({ style, onClick, title }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      data-btn="true"
      title={title}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={e => { e.stopPropagation(); onClick(); }}
      style={{
        position: "absolute",
        width: BTN_R * 2, height: BTN_R * 2,
        borderRadius: "50%",
        background: hov ? "#3A7BD5" : "white",
        border: `1.5px solid ${hov ? "#3A7BD5" : "#CBD5E1"}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", zIndex: 20,
        transition: "background .12s, border-color .12s",
        boxShadow: "0 1px 4px rgba(28,43,74,0.12)",
        ...style,
      }}
    >
      <span style={{ fontSize: 14, lineHeight: 1, color: hov ? "white" : "#9CA3AF", fontWeight: 400 }}>+</span>
    </div>
  );
}

function NodeCard({
  node, pos, isSelected, isFirst, parentLabel,
  onClick, onAddRight, onAddBottom, onAddLeft, onDelete, onRename,
}) {
  const [hovered, setHovered]     = useState(false);
  const [editing, setEditing]     = useState(false);
  const [draft,   setDraft]       = useState(node.label);
  const inputRef                  = useRef(null);
  const style = getNodeStyle(node, isSelected);

  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  function commitEdit() {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== node.label) onRename(node.id, trimmed);
    setEditing(false);
  }

  function handleDelete() {
    const kidCount = (node.children || []).length;
    if (kidCount > 0) {
      if (!window.confirm(`"${node.label}" has ${kidCount} child${kidCount > 1 ? "ren" : ""}. Deleting it will also remove all its children. Continue?`)) return;
    }
    onDelete();
  }

  return (
    <div
      data-node="true"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); }}
      style={{ ...style, left: pos.x, top: pos.y }}
      onClick={() => { if (!editing) onClick(); }}
      onDoubleClick={e => { e.stopPropagation(); setDraft(node.label); setEditing(true); }}
    >
      {/* Label — inline editable on double-click */}
      {editing ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={e => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") { setDraft(node.label); setEditing(false); } }}
          onClick={e => e.stopPropagation()}
          style={{
            fontSize: 16, fontWeight: 600, color: "#1C2B4A",
            border: "none", borderBottom: "1.5px solid #3A7BD5",
            outline: "none", background: "transparent",
            width: "100%", fontFamily: "'DM Sans', sans-serif",
            lineHeight: 1.3, padding: 0,
          }}
        />
      ) : (
        <div style={{
          fontSize: 16, fontWeight: 600, color: "#1C2B4A",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: 1.3,
        }}>{node.label}</div>
      )}

      {/* Sub-label: parent name */}
      {parentLabel && !editing && (
        <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {parentLabel}
        </div>
      )}

      {/* Selection ring */}
      {isSelected && (
        <div style={{ position: "absolute", inset: -4, borderRadius: 14,
          border: "2px solid rgba(58,123,213,0.3)", pointerEvents: "none" }}/>
      )}

      {/* Hover controls */}
      {hovered && !editing && (
        <>
          {/* Delete — top-right */}
          <div
            data-btn="true"
            title="Delete node"
            onClick={e => { e.stopPropagation(); handleDelete(); }}
            style={{
              position: "absolute", top: -9, right: -9,
              width: 20, height: 20, borderRadius: "50%",
              background: "#FEE2E2", border: "1.5px solid #FECACA",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", zIndex: 21, transition: "background .12s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#EF4444"; e.currentTarget.querySelector("span").style.color = "white"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#FEE2E2"; e.currentTarget.querySelector("span").style.color = "#EF4444"; }}
          >
            <span style={{ fontSize: 12, lineHeight: 1, color: "#EF4444", fontWeight: 700 }}>×</span>
          </div>

          {/* Right: add sibling */}
          <PlusBtn title="Add sibling to the right" onClick={onAddRight}
            style={{ right: -(BTN_R * 2 + 2), top: "50%", transform: "translateY(-50%)" }}/>

          {/* Bottom: add child */}
          <PlusBtn title="Add child" onClick={onAddBottom}
            style={{ bottom: -(BTN_R * 2 + 2), left: "50%", transform: "translateX(-50%)" }}/>

          {/* Left: only if NOT the first in its row */}
          {!isFirst && (
            <PlusBtn title="Add sibling to the left" onClick={onAddLeft}
              style={{ left: -(BTN_R * 2 + 2), top: "50%", transform: "translateY(-50%)" }}/>
          )}
        </>
      )}
    </div>
  );
}

// ─── Org Tab ──────────────────────────────────────────────────────────────────
function OrgTab({ org, isActive, onClick, onRename }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(org.label);
  const inputRef = useRef(null);

  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  function commit() {
    const trimmed = draft.trim();
    if (trimmed) onRename(org.id, trimmed);
    setEditing(false);
  }

  return (
    <Box
      onClick={() => { if (!editing) onClick(org.id); }}
      onDoubleClick={() => { setDraft(org.label); setEditing(true); }}
      sx={{
        display: "flex", alignItems: "center", gap: 0.75,
        px: editing ? 1 : 1.5, py: 0,
        height: "100%",
        borderBottom: isActive ? "2px solid #3A7BD5" : "2px solid transparent",
        color: isActive ? "#1C2B4A" : "#6B7A99",
        fontWeight: isActive ? 700 : 400,
        fontSize: 13,
        cursor: "pointer",
        userSelect: "none",
        flexShrink: 0,
        transition: "color .15s, border-color .15s",
        "&:hover": { color: "#1C2B4A" },
        position: "relative",
      }}
    >
      {editing ? (
        <Box display="flex" alignItems="center" gap={0.5}>
          <input
            ref={inputRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
            onBlur={commit}
            style={{
              border: "1px solid #3A7BD5", borderRadius: 6, padding: "2px 8px",
              fontSize: 13, fontFamily: "'DM Sans', sans-serif", outline: "none",
              width: Math.max(80, draft.length * 8 + 20),
              fontWeight: 600, color: "#1C2B4A",
            }}
          />
        </Box>
      ) : (
        <Typography fontSize={13} fontWeight={isActive ? 700 : 500}
          color={isActive ? "text.primary" : "text.secondary"}
          sx={{ whiteSpace: "nowrap" }}>
          {org.label}
        </Typography>
      )}
    </Box>
  );
}

// ─── Permission drawer ────────────────────────────────────────────────────────
const NODE_TYPE_LABEL = {
  category: "Category",
  role:     "Role",
  group:    "Group",
  leaf:     "Item",
};

const NODE_TYPE_COLOR = {
  category: { bg: "#F0FDF4", icon: "#16A34A", text: "#15803D" },
  role:     { bg: "#EEF2FF", icon: "#4F46E5", text: "#4338CA" },
  group:    { bg: "#FFF7ED", icon: "#EA580C", text: "#C2410C" },
  leaf:     { bg: "#F0F9FF", icon: "#0284C7", text: "#0369A1" },
};

function PermissionDrawer({ role: node, allRoles, orgNode, onToggle, onClose, onRename }) {
  const isOpen = !!node;
  const hasPerms = node?.permissions != null;
  const [editingName, setEditingName] = useState(false);
  const [nameDraft,   setNameDraft]   = useState("");
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (editingName && nameInputRef.current) nameInputRef.current.focus();
  }, [editingName]);

  // Reset editing state when node changes
  useEffect(() => { setEditingName(false); }, [node?.id]);

  function commitName() {
    const t = nameDraft.trim();
    if (t && t !== node?.label) onRename(node.id, t);
    setEditingName(false);
  }

  const ancestorIds = isOpen && hasPerms
    ? (getAncestorRoles(orgNode, node.id, []) || [])
    : [];

  function isBlocked(key) {
    return ancestorIds.some(aid => {
      const anc = allRoles.find(r => r.id === aid);
      return anc && anc.permissions[key] === false;
    });
  }

  const sections = [...new Set(FLAT_PERMS.map(p => p.section))];
  const typeColors = NODE_TYPE_COLOR[node?.type] || NODE_TYPE_COLOR.leaf;

  return (
    <>
      <Box sx={{
        position: "fixed", top: 0, right: 0, width: PANEL_W, height: "100vh",
        zIndex: 1200, display: "flex", flexDirection: "column",
        background: "white", borderLeft: "1px solid #E4E8F0",
        boxShadow: isOpen ? "-8px 0 32px rgba(28,43,74,0.12)" : "none",
        transform: isOpen ? "translateX(0)" : `translateX(${PANEL_W}px)`,
        transition: "transform 0.32s cubic-bezier(0.4,0,0.2,1), box-shadow 0.32s ease",
        willChange: "transform",
      }}>
        {/* Header */}
        <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: "1px solid #E4E8F0", flexShrink: 0 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
              {hasPerms ? "Access / Feature Control" : "Access / Feature Control"}
            </Typography>
            <IconButton size="small" onClick={onClose}
              sx={{ width: 28, height: 28, color: "#6B7A99", "&:hover": { background: "#F1F5F9" } }}>
              <Close fontSize="small"/>
            </IconButton>
          </Box>
          {node && (
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box sx={{
                width: 36, height: 36, borderRadius: "50%",
                background: typeColors.bg, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Person sx={{ fontSize: 18, color: typeColors.icon }}/>
              </Box>
              <Box flex={1} minWidth={0}>
                {editingName ? (
                  <input
                    ref={nameInputRef}
                    value={nameDraft}
                    onChange={e => setNameDraft(e.target.value)}
                    onBlur={commitName}
                    onKeyDown={e => { if (e.key === "Enter") commitName(); if (e.key === "Escape") setEditingName(false); }}
                    style={{
                      fontSize: 14, fontWeight: 700, color: "#1C2B4A",
                      border: "none", borderBottom: "1.5px solid #3A7BD5",
                      outline: "none", background: "transparent",
                      width: "100%", fontFamily: "'DM Sans', sans-serif",
                      padding: 0,
                    }}
                  />
                ) : (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" fontWeight={700} lineHeight={1.2}
                      sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {node.label}
                    </Typography>
                    <IconButton size="small"
                      onClick={() => { setNameDraft(node.label); setEditingName(true); }}
                      sx={{ width: 20, height: 20, color: "#9CA3AF", flexShrink: 0,
                        "&:hover": { color: "#3A7BD5", background: "transparent" } }}>
                      <Edit sx={{ fontSize: 12 }}/>
                    </IconButton>
                  </Box>
                )}
                <Typography variant="caption" sx={{ color: typeColors.text, fontWeight: 500 }}>
                  {NODE_TYPE_LABEL[node.type] || "Node"}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>
          {node && hasPerms && sections.map(sec => (
            <Box key={sec} mb={2.5}>
              <Typography variant="caption" fontWeight={700} color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 1, display: "block", mb: 1 }}>
                {sec}
              </Typography>
              {FLAT_PERMS.filter(p => p.section === sec).map(p => {
                const blocked = isBlocked(p.key);
                const checked = node.permissions[p.key] ?? false;
                return (
                  <Box key={p.key} display="flex" alignItems="center"
                    sx={{ pl: p.parent ? 2.5 : 0, opacity: blocked ? 0.38 : 1 }}>
                    {p.parent && (
                      <Box sx={{ width: 10, height: 10, borderLeft: "1px solid #CBD5E1",
                        borderBottom: "1px solid #CBD5E1", mr: 0.75, mb: 0.75, flexShrink: 0 }}/>
                    )}
                    <FormControlLabel sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 13, color: "#374151" } }}
                      control={
                        <Checkbox size="small" checked={checked && !blocked} disabled={blocked}
                          onChange={() => !blocked && onToggle(node.id, p.key)}
                          sx={{ py: 0.4, color: "#D1D5DB", "&.Mui-checked": { color: "#3A7BD5" } }}/>
                      }
                      label={p.key}
                    />
                  </Box>
                );
              })}
            </Box>
          ))}

          {node && !hasPerms && (
            <Box>
              <Box sx={{ p: 2, background: "#F8FAFC", borderRadius: 2, border: "1px solid #E4E8F0", mb: 2 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary"
                  sx={{ textTransform: "uppercase", letterSpacing: 1, display: "block", mb: 1.5 }}>
                  Properties
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">Type</Typography>
                    <Typography variant="caption" fontWeight={600} sx={{ color: typeColors.text }}>
                      {NODE_TYPE_LABEL[node.type] || node.type}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">Label</Typography>
                    <Typography variant="caption" fontWeight={600} color="text.primary">{node.label}</Typography>
                  </Box>
                  {node.dynamic !== undefined && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">Mode</Typography>
                      <Typography variant="caption" fontWeight={600} color="text.primary">
                        {node.dynamic ? "Dynamic" : "Static"}
                      </Typography>
                    </Box>
                  )}
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">Children</Typography>
                    <Typography variant="caption" fontWeight={600} color="text.primary">
                      {(node.children || []).length}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary" lineHeight={1.7}>
                This node type does not have configurable access permissions.
                Only <strong>Role</strong> nodes carry permission settings.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer */}
        {hasPerms && (
          <Box sx={{ px: 2.5, py: 2, borderTop: "1px solid #E4E8F0", flexShrink: 0, background: "#F8FAFC" }}>
            <Typography variant="caption" color="text.secondary" lineHeight={1.7} display="block">
              <strong>[X]</strong> checked · all unchecked by default.<br/>
              Parent unchecked = locked off for all children.
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
}

// ─── Canvas for one org ───────────────────────────────────────────────────────
function OrgCanvas({ org, onUpdateOrg }) {
  const [selectedId, setSelectedId] = useState(null);
  const [scale, setScale]           = useState(1);
  const [offset, setOffset]         = useState({ x: 0, y: 0 });
  const wrapRef = useRef(null);
  const panRef  = useRef(null);

  const allRoles    = getAllRoles(org);
  const selectedNode = selectedId ? (findNode(org, selectedId) ?? null) : null;
  const selectedRole = selectedNode; // keep same name for PermissionDrawer prop

  // Skip the org root node itself; layout starts from org's children
  // We create a synthetic root that IS the org so the layout engine
  // places categories directly.
  const positions  = computeLayout(org);
  const { w: cW, h: cH } = canvasDimensions(positions);

  // Remove the org node from the canvas render — we only show its children
  const orgPos = positions.get(org.id);

  useEffect(() => {
    if (!wrapRef.current) return;
    const { width, height } = wrapRef.current.getBoundingClientRect();
    const s = Math.min(1, (width - 40) / cW, (height - 40) / cH);
    setScale(s);
    setOffset({ x: 20, y: 20 });
  }, [org.id]); // reset view when switching tabs

  useEffect(() => {
    if (!wrapRef.current) return;
    const el = wrapRef.current;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }); // re-attach every render so onWheel closure has fresh scale/offset

  // Pan
  function onMouseDown(e) {
    if (e.button !== 0) return;
    const t = e.target;
    if (t.closest && (t.closest("[data-node]") || t.closest("[data-btn]"))) return;
    panRef.current = { sx: e.clientX - offset.x, sy: e.clientY - offset.y };
    wrapRef.current.style.cursor = "grabbing";
  }
  function onMouseMove(e) {
    if (!panRef.current) return;
    setOffset({ x: e.clientX - panRef.current.sx, y: e.clientY - panRef.current.sy });
  }
  function onMouseUp() {
    if (!panRef.current) return;
    panRef.current = null;
    if (wrapRef.current) wrapRef.current.style.cursor = "grab";
  }

  // Zoom
  function onWheel(e) {
    e.preventDefault();
    const rect = wrapRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left, my = e.clientY - rect.top;
    const f = e.deltaY < 0 ? 1.08 : 0.93;
    const ns = Math.min(2.5, Math.max(0.2, scale * f));
    setOffset({ x: mx - (mx - offset.x) * ns / scale, y: my - (my - offset.y) * ns / scale });
    setScale(ns);
  }
  function zoomBy(delta) {
    if (!wrapRef.current) return;
    const { width, height } = wrapRef.current.getBoundingClientRect();
    const mx = width / 2, my = height / 2;
    const ns = Math.min(2.5, Math.max(0.2, scale + delta));
    setOffset({ x: mx - (mx - offset.x) * ns / scale, y: my - (my - offset.y) * ns / scale });
    setScale(ns);
  }
  function resetView() {
    if (!wrapRef.current) return;
    const { width, height } = wrapRef.current.getBoundingClientRect();
    const ns = Math.min(1, (width - 40) / cW, (height - 40) / cH);
    setScale(ns);
    setOffset({ x: 20, y: 20 });
  }

  function handleNodeClick(node) {
    setSelectedId(prev => prev === node.id ? null : node.id);
  }

  function togglePerm(roleId, permKey) {
    const next = JSON.parse(JSON.stringify(org));
    const role = findNode(next, roleId);
    if (role?.permissions) {
      role.permissions[permKey] = !role.permissions[permKey];
      if (!role.permissions[permKey]) {
        function cascade(n) {
          for (const c of (n.children || [])) {
            if (c.permissions) c.permissions[permKey] = false;
            cascade(c);
          }
        }
        cascade(role);
      }
    }
    onUpdateOrg(next);
  }

  // Build a parent-map so we can find the parent of any node quickly
  function buildParentMap(node, map = new Map()) {
    for (const child of (node.children || [])) {
      map.set(child.id, node);
      buildParentMap(child, map);
    }
    return map;
  }

  function makeNewNode(parentType) {
    const id = "n-" + uid();
    const isRole = parentType === "role" || parentType === "group" || parentType === "category";
    return {
      id, label: "New Node", type: isRole ? "role" : "leaf",
      children: [],
      ...(isRole ? { permissions: makePerms([]) } : {}),
    };
  }

  function addSiblingRight(node) {
    const next = JSON.parse(JSON.stringify(org));
    const parentMap = buildParentMap(next);
    const parent = parentMap.get(node.id);
    if (!parent) return;
    const idx = parent.children.findIndex(c => c.id === node.id);
    const newNode = makeNewNode(node.type);
    parent.children.splice(idx + 1, 0, newNode);
    onUpdateOrg(next);
  }

  function addSiblingLeft(node) {
    const next = JSON.parse(JSON.stringify(org));
    const parentMap = buildParentMap(next);
    const parent = parentMap.get(node.id);
    if (!parent) return;
    const idx = parent.children.findIndex(c => c.id === node.id);
    const newNode = makeNewNode(node.type);
    parent.children.splice(Math.max(0, idx), 0, newNode);
    onUpdateOrg(next);
  }

  function addChildBelow(node) {
    const next = JSON.parse(JSON.stringify(org));
    const target = findNode(next, node.id);
    if (!target) return;
    const newNode = makeNewNode(node.type);
    target.children.push(newNode);
    onUpdateOrg(next);
  }

  function deleteNode(node) {
    const next = JSON.parse(JSON.stringify(org));
    const parentMap = buildParentMap(next);
    const parent = parentMap.get(node.id);
    if (!parent) return;
    parent.children = parent.children.filter(c => c.id !== node.id);
    onUpdateOrg(next);
    if (selectedId === node.id) setSelectedId(null);
  }

  function renameNode(nodeId, newLabel) {
    const next = JSON.parse(JSON.stringify(org));
    const target = findNode(next, nodeId);
    if (target) target.label = newLabel;
    onUpdateOrg(next);
  }

  // Build parent map for render-time lookups (isFirst, parentLabel)
  const renderParentMap = buildParentMap(org);

  // Collect nodes (skip org root node itself — it's a tab)
  const nodeList = [];
  const parentGroups = [];

  function collect(node, isRoot = false) {
    const pos = positions.get(node.id);
    if (pos && !isRoot) nodeList.push({ pos, node });
    const kids = node.children || [];
    if (kids.length > 0 && pos) {
      const childPositions = kids.map(c => positions.get(c.id)).filter(Boolean);
      if (childPositions.length && !isRoot) {
        parentGroups.push({ parentPos: pos, childPositions });
      }
    }
    for (const child of kids) collect(child, false);
  }
  collect(org, true);

  // Row stripes
  const rowIndexYMap = new Map();
  positions.forEach(({ y, rowIndex, node }) => {
    if (node.id === org.id) return; // skip org root
    if (!rowIndexYMap.has(rowIndex)) rowIndexYMap.set(rowIndex, y);
  });
  const rowYs = [...rowIndexYMap.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([rowIndex, y]) => ({ rowIndex, y }));

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Zoom bar */}
      <Box sx={{
        height: 40, flexShrink: 0, background: "#F8FAFC",
        borderBottom: "1px solid #E4E8F0",
        display: "flex", alignItems: "center", px: 2, gap: 1,
      }}>
        <Box sx={{ flex: 1 }}/>
        {!selectedRole && (
          <Chip icon={<ChevronRight sx={{ fontSize: "13px !important" }}/>}
            label="Click any node to inspect or configure" size="small"
            sx={{ fontSize: 11, height: 22, background: "#EEF2FF", color: "#4338CA", border: "1px solid #C7D2FE" }}/>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5,
          background: "white", border: "1px solid #E2E8F0", borderRadius: "8px", px: 0.75, py: 0.5 }}>
          <Tooltip title="Zoom out"><IconButton size="small" onClick={() => zoomBy(-0.1)} sx={{ width: 26, height: 26 }}><ZoomOut fontSize="small"/></IconButton></Tooltip>
          <Typography fontSize={11} fontWeight={600} color="text.secondary" sx={{ minWidth: 36, textAlign: "center" }}>
            {Math.round(scale * 100)}%
          </Typography>
          <Tooltip title="Zoom in"><IconButton size="small" onClick={() => zoomBy(0.1)} sx={{ width: 26, height: 26 }}><ZoomIn fontSize="small"/></IconButton></Tooltip>
          <Box sx={{ width: 1, height: 14, background: "#E2E8F0", mx: 0.5 }}/>
          <Tooltip title="Fit to screen"><IconButton size="small" onClick={resetView} sx={{ width: 26, height: 26 }}><CenterFocusStrong fontSize="small"/></IconButton></Tooltip>
        </Box>
      </Box>

      {/* Canvas */}
      <Box ref={wrapRef}
        sx={{ flex: 1, position: "relative", overflow: "hidden", cursor: "grab" }}
        onMouseDown={onMouseDown} onMouseMove={onMouseMove}
        onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
      >
        {/* Lane stripes */}
        <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 0 }}>
          {rowYs.map(({ rowIndex, y: rawY }, idx) => {
            const stripeTop    = (rawY - (ROW_H - NODE_H) / 2) * scale + offset.y;
            const stripeHeight = ROW_H * scale;
            return (
              <g key={`lane-${rowIndex}`}>
                <rect x={0} y={stripeTop} width="100%" height={stripeHeight}
                  fill={LANE_TINTS[idx % LANE_TINTS.length]}/>
                {idx > 0 && (
                  <line x1={0} y1={stripeTop} x2="100%" y2={stripeTop}
                    stroke="#E2E8F0" strokeWidth="1"/>
                )}
              </g>
            );
          })}
        </svg>

        {/* Dot grid */}
        <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 1 }}>
          <defs>
            <pattern id="dots" x={offset.x % (20 * scale)} y={offset.y % (20 * scale)}
              width={20 * scale} height={20 * scale} patternUnits="userSpaceOnUse">
              <circle cx={1} cy={1} r={0.9} fill="#CBD5E1" opacity="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dots)"/>
        </svg>

        {/* Transformed canvas */}
        <div style={{
          position: "absolute", top: 0, left: 0,
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
          transformOrigin: "0 0", width: cW, height: cH,
        }}>
          {/* Connectors */}
          <svg width={cW} height={cH}
            style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", overflow: "visible" }}>
            {parentGroups.map(({ parentPos, childPositions }) => (
              <ParentConnectors key={`stem-${parentPos.node.id}`}
                parentPos={parentPos} childPositions={childPositions}/>
            ))}
          </svg>

          {/* Add-between buttons — REMOVED (now on hover per card) */}

          {/* Node cards with hover + buttons */}
          {nodeList.map(({ pos, node }) => {
            const isSelected  = selectedId === node.id;
            const parent      = renderParentMap.get(node.id);
            const parentLabel = parent ? parent.label : null;
            // isFirst: this node is the first child of its parent
            const siblings    = parent ? (parent.children || []) : [];
            const isFirst     = siblings.length === 0 || siblings[0]?.id === node.id;
            return (
              <NodeCard
                key={node.id}
                node={node}
                pos={pos}
                isSelected={isSelected}
                isFirst={isFirst}
                parentLabel={parentLabel}
                onClick={() => handleNodeClick(node)}
                onAddRight={() => addSiblingRight(node)}
                onAddBottom={() => addChildBelow(node)}
                onAddLeft={() => addSiblingLeft(node)}
                onDelete={() => deleteNode(node)}
                onRename={(id, label) => renameNode(id, label)}
              />
            );
          })}
        </div>

        {/* Lane labels */}
        <div style={{ position: "absolute", left: 0, top: 0, width: 120, height: "100%", pointerEvents: "none", zIndex: 10 }}>
          {rowYs.map(({ rowIndex, y: rawY }, idx) => {
            const screenY = rawY * scale + offset.y;
            const centreY = screenY + (NODE_H * scale) / 2;
            // Find the depth of any node on this row
            let rowDepth = null;
            positions.forEach(({ y, node, depth }) => {
              if (y === rawY && node.id !== org.id && rowDepth === null) rowDepth = depth;
            });
            const rowLabel = rowDepth !== null ? ROW_LABELS[rowDepth] : null;
            // Hide the pill if no label is configured for this depth
            if (!rowLabel) return null;
            return (
              <div key={`lbl-${rowIndex}`} style={{
                position: "absolute", top: centreY - 11, left: 8,
                background: "white", border: "1px solid #E2E8F0",
                borderRadius: 20, padding: "3px 12px",
                fontSize: 9, fontWeight: 700, color: "#94A3B8",
                letterSpacing: 0.8, fontFamily: "'DM Sans', sans-serif",
                whiteSpace: "nowrap", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                textTransform: "uppercase",
              }}>
                {rowLabel}
              </div>
            );
          })}
        </div>
      </Box>

      {/* Permission drawer */}
      <PermissionDrawer
        role={selectedRole}
        allRoles={allRoles}
        orgNode={org}
        onToggle={togglePerm}
        onClose={() => setSelectedId(null)}
        onRename={renameNode}
      />
    </Box>
  );
}

// ─── Root TagBoard ────────────────────────────────────────────────────────────
export default function TagBoard() {
  const [orgs,      setOrgs]      = useState(initialOrgs);
  const [activeId,  setActiveId]  = useState(initialOrgs[0].id);

  const activeOrg = orgs.find(o => o.id === activeId) ?? orgs[0];

  function addOrg() {
    const id  = "org-" + uid();
    const num = orgs.length + 1;
    const newOrg = makeOrg(id, `Organization ${num}`);
    setOrgs(prev => [...prev, newOrg]);
    setActiveId(id);
  }

  function renameOrg(id, label) {
    setOrgs(prev => prev.map(o => o.id === id ? { ...o, label } : o));
  }

  function updateOrg(updated) {
    setOrgs(prev => prev.map(o => o.id === updated.id ? updated : o));
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline/>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet"/>

          <Box sx={{ height: 'calc(94vh)', display: 'flex', flexDirection: 'column', gap: 2 }}>


        {/* ── App bar ──────────────────────────────────────────────────────── */}
        {/* <Box sx={{
          flexShrink: 0, background: "white",
          borderBottom: "1px solid #E4E8F0",
          px: 3, py: 2,
          display: "flex", alignItems: "center", gap: 2,
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f2137', mb: 0.25 }}>
              Tag Board
            </Typography>
            <Typography variant="body2" sx={{ color: '#4a6a85' }}>
              Add organization charts and tags
            </Typography>
          </Box>
        </Box> */}

        {/* ── Org tabs bar ─────────────────────────────────────────────────── */}
        <Box sx={{
          height: 44, flexShrink: 0, background: "white",
          borderBottom: "1px solid #E4E8F0",
          display: "flex", alignItems: "stretch",
          px: 1.5, gap: 0, overflowX: "auto",
          "&::-webkit-scrollbar": { height: 0 },
        }}>
          {orgs.map(org => (
            <OrgTab
              key={org.id}
              org={org}
              isActive={org.id === activeId}
              onClick={setActiveId}
              onRename={renameOrg}
            />
          ))}

          {/* Add org tab button */}
          <Tooltip title="New organization board" placement="bottom">
            <IconButton
              size="small"
              onClick={addOrg}
              sx={{
                width: 30, height: 30, my: "auto", ml: 0.5, flexShrink: 0,
                border: "1.5px dashed #CBD5E1", color: "#9CA3AF",
                borderRadius: "8px",
                "&:hover": { borderColor: "#3A7BD5", color: "#3A7BD5", background: "#EEF6FF" },
              }}>
              <Add fontSize="small"/>
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── Canvas for active org ────────────────────────────────────────── */}
        <OrgCanvas
          key={activeOrg.id}
          org={activeOrg}
          onUpdateOrg={updateOrg}
        />

      </Box>
    </ThemeProvider>
  );
}